import {
  ContextMenu, ContextMenuTrigger, ContextMenuContent,
  ContextMenuItem, ContextMenuSeparator, ContextMenuSub,
  ContextMenuSubTrigger, ContextMenuSubContent,
} from './ui/context-menu';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubTrigger, DropdownMenuSubContent,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import {
  Edit3, Copy, Move, Lock, Unlock, Archive, Trash2,
  Tag as TagIcon, Palette, MoreVertical, RotateCcw, FolderTree,
  CalendarPlus, Star, Link2, Shield, Pin, PinOff, Download, Share2,
} from 'lucide-react';

const COLORS = [
  { value: 'gray',   hex: '#94a3b8', label: 'Gray' },
  { value: 'red',    hex: '#ef4444', label: 'Red' },
  { value: 'orange', hex: '#f97316', label: 'Orange' },
  { value: 'amber',  hex: '#f59e0b', label: 'Amber' },
  { value: 'green',  hex: '#22c55e', label: 'Green' },
  { value: 'blue',   hex: '#3b82f6', label: 'Blue' },
  { value: 'purple', hex: '#a855f7', label: 'Purple' },
  { value: 'pink',   hex: '#ec4899', label: 'Pink' },
];

export const FOLDER_COLORS = COLORS;

/**
 * Renders both a right-click ContextMenu (wrapping `children`) AND a 3-dot
 * dropdown trigger (returned next to the children). Uses a callback bag for
 * each action so the parent can plumb through the API call + state refresh.
 */
export default function FolderActions({ folder, onAction, children, hideTrigger = false }) {
  if (!folder) return children || null;
  const isContactRoot = folder.is_contact_root;
  const isLocked = folder.is_locked;
  const inTrash = folder.is_deleted;
  const inArchive = folder.is_archived && !folder.is_deleted;

  const dispatch = (a, payload) => onAction?.(a, folder, payload);

  const items = (
    <>
      {inTrash ? (
        <>
          <ContextMenuItem onClick={() => dispatch('restore')} data-testid={`fa-restore-${folder.folder_id}`}>
            <RotateCcw className="w-3.5 h-3.5 mr-2" /> Restore
          </ContextMenuItem>
          <ContextMenuItem className="text-destructive" onClick={() => dispatch('permanent_delete')}
            data-testid={`fa-permadelete-${folder.folder_id}`}>
            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete permanently
          </ContextMenuItem>
        </>
      ) : inArchive ? (
        <ContextMenuItem onClick={() => dispatch('restore')} data-testid={`fa-unarchive-${folder.folder_id}`}>
          <RotateCcw className="w-3.5 h-3.5 mr-2" /> Restore from archive
        </ContextMenuItem>
      ) : (
        <>
          <ContextMenuItem disabled={isLocked || isContactRoot} onClick={() => dispatch('rename')}
            data-testid={`fa-rename-${folder.folder_id}`}>
            <Edit3 className="w-3.5 h-3.5 mr-2" /> Rename
          </ContextMenuItem>
          <ContextMenuItem disabled={isLocked || isContactRoot} onClick={() => dispatch('move')}
            data-testid={`fa-move-${folder.folder_id}`}>
            <Move className="w-3.5 h-3.5 mr-2" /> Move to…
          </ContextMenuItem>
          <ContextMenuItem onClick={() => dispatch('duplicate')} data-testid={`fa-duplicate-${folder.folder_id}`}>
            <Copy className="w-3.5 h-3.5 mr-2" /> Duplicate
          </ContextMenuItem>

          <ContextMenuSub>
            <ContextMenuSubTrigger><Palette className="w-3.5 h-3.5 mr-2" /> Color</ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {COLORS.map(c => (
                <ContextMenuItem key={c.value} onClick={() => dispatch('color', c.hex)}
                  data-testid={`fa-color-${c.value}-${folder.folder_id}`}>
                  <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ background: c.hex }} />
                  {c.label}
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>

          <ContextMenuItem onClick={() => dispatch('tags')} data-testid={`fa-tags-${folder.folder_id}`}>
            <TagIcon className="w-3.5 h-3.5 mr-2" /> Edit tags…
          </ContextMenuItem>

          <ContextMenuItem disabled={isContactRoot} onClick={() => dispatch('save_template')}
            data-testid={`fa-save-template-${folder.folder_id}`}>
            <FolderTree className="w-3.5 h-3.5 mr-2" /> Save as template…
          </ContextMenuItem>

          <ContextMenuItem disabled={isLocked} onClick={() => dispatch('rollover')}
            data-testid={`fa-rollover-${folder.folder_id}`}>
            <CalendarPlus className="w-3.5 h-3.5 mr-2" /> Roll forward to next year…
          </ContextMenuItem>

          <ContextMenuItem onClick={() => dispatch('toggle_star')} data-testid={`fa-star-${folder.folder_id}`}>
            <Star className={cn('w-3.5 h-3.5 mr-2', folder.is_starred && 'fill-amber-400 text-amber-400')} />
            {folder.is_starred ? 'Unstar' : 'Star'}
          </ContextMenuItem>
          {((folder.linked_contacts || []).length > 0 || (folder.linked_contact_ids || []).length > 0) && (
            <ContextMenuItem onClick={() => dispatch('toggle_pin')} data-testid={`fa-pin-${folder.folder_id}`}>
              {folder.is_pinned
                ? <><PinOff className="w-3.5 h-3.5 mr-2" /> Unpin from contact profile</>
                : <><Pin className="w-3.5 h-3.5 mr-2" /> Pin to contact profile</>}
            </ContextMenuItem>
          )}
          <ContextMenuItem onClick={() => dispatch('shortcut')} data-testid={`fa-shortcut-${folder.folder_id}`}>
            <Link2 className="w-3.5 h-3.5 mr-2" /> Create shortcut…
          </ContextMenuItem>
          <ContextMenuItem onClick={() => dispatch('permissions')} data-testid={`fa-permissions-${folder.folder_id}`}>
            <Shield className="w-3.5 h-3.5 mr-2" /> Share & permissions…
          </ContextMenuItem>
          <ContextMenuItem onClick={() => dispatch('export_zip')} data-testid={`fa-export-zip-${folder.folder_id}`}>
            <Download className="w-3.5 h-3.5 mr-2" /> Export folder (ZIP)
          </ContextMenuItem>
          <ContextMenuItem onClick={() => dispatch('public_link')} data-testid={`fa-public-${folder.folder_id}`}>
            <Share2 className={cn('w-3.5 h-3.5 mr-2', folder.public_share_token && 'text-emerald-500')} />
            {folder.public_share_token ? 'Manage public link' : 'Create public link…'}
          </ContextMenuItem>

          <ContextMenuSeparator />

          <ContextMenuItem onClick={() => dispatch(isLocked ? 'unlock' : 'lock')}
            data-testid={`fa-lock-${folder.folder_id}`}>
            {isLocked
              ? <><Unlock className="w-3.5 h-3.5 mr-2" /> Unlock</>
              : <><Lock className="w-3.5 h-3.5 mr-2" /> Lock</>}
          </ContextMenuItem>
          <ContextMenuItem disabled={isLocked || isContactRoot} onClick={() => dispatch('archive')}
            data-testid={`fa-archive-${folder.folder_id}`}>
            <Archive className="w-3.5 h-3.5 mr-2" /> Archive
          </ContextMenuItem>
          <ContextMenuItem disabled={isLocked || isContactRoot} className="text-destructive"
            onClick={() => dispatch('delete')} data-testid={`fa-delete-${folder.folder_id}`}>
            <Trash2 className="w-3.5 h-3.5 mr-2" /> Move to Trash
          </ContextMenuItem>
        </>
      )}
    </>
  );

  // Build the same items for the dropdown menu (different primitive)
  const dropdownItems = (
    <>
      {inTrash ? (
        <>
          <DropdownMenuItem onClick={() => dispatch('restore')}>
            <RotateCcw className="w-3.5 h-3.5 mr-2" /> Restore
          </DropdownMenuItem>
          <DropdownMenuItem className="text-destructive" onClick={() => dispatch('permanent_delete')}>
            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete permanently
          </DropdownMenuItem>
        </>
      ) : inArchive ? (
        <DropdownMenuItem onClick={() => dispatch('restore')}>
          <RotateCcw className="w-3.5 h-3.5 mr-2" /> Restore from archive
        </DropdownMenuItem>
      ) : (
        <>
          <DropdownMenuItem disabled={isLocked || isContactRoot} onClick={() => dispatch('rename')}>
            <Edit3 className="w-3.5 h-3.5 mr-2" /> Rename
          </DropdownMenuItem>
          <DropdownMenuItem disabled={isLocked || isContactRoot} onClick={() => dispatch('move')}>
            <Move className="w-3.5 h-3.5 mr-2" /> Move to…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => dispatch('duplicate')}>
            <Copy className="w-3.5 h-3.5 mr-2" /> Duplicate
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger><Palette className="w-3.5 h-3.5 mr-2" /> Color</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {COLORS.map(c => (
                <DropdownMenuItem key={c.value} onClick={() => dispatch('color', c.hex)}>
                  <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ background: c.hex }} />
                  {c.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem onClick={() => dispatch('tags')}>
            <TagIcon className="w-3.5 h-3.5 mr-2" /> Edit tags…
          </DropdownMenuItem>
          <DropdownMenuItem disabled={isContactRoot} onClick={() => dispatch('save_template')}>
            <FolderTree className="w-3.5 h-3.5 mr-2" /> Save as template…
          </DropdownMenuItem>
          <DropdownMenuItem disabled={isLocked} onClick={() => dispatch('rollover')}>
            <CalendarPlus className="w-3.5 h-3.5 mr-2" /> Roll forward to next year…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => dispatch('toggle_star')}>
            <Star className={cn('w-3.5 h-3.5 mr-2', folder.is_starred && 'fill-amber-400 text-amber-400')} />
            {folder.is_starred ? 'Unstar' : 'Star'}
          </DropdownMenuItem>
          {((folder.linked_contacts || []).length > 0 || (folder.linked_contact_ids || []).length > 0) && (
            <DropdownMenuItem onClick={() => dispatch('toggle_pin')}>
              {folder.is_pinned
                ? <><PinOff className="w-3.5 h-3.5 mr-2" /> Unpin from contact profile</>
                : <><Pin className="w-3.5 h-3.5 mr-2" /> Pin to contact profile</>}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => dispatch('shortcut')}>
            <Link2 className="w-3.5 h-3.5 mr-2" /> Create shortcut…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => dispatch('permissions')}>
            <Shield className="w-3.5 h-3.5 mr-2" /> Share & permissions…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => dispatch('export_zip')} data-testid={`fa-export-zip-${folder.folder_id}`}>
            <Download className="w-3.5 h-3.5 mr-2" /> Export folder (ZIP)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => dispatch('public_link')}>
            <Share2 className={cn('w-3.5 h-3.5 mr-2', folder.public_share_token && 'text-emerald-500')} />
            {folder.public_share_token ? 'Manage public link' : 'Create public link…'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => dispatch(isLocked ? 'unlock' : 'lock')}>
            {isLocked
              ? <><Unlock className="w-3.5 h-3.5 mr-2" /> Unlock</>
              : <><Lock className="w-3.5 h-3.5 mr-2" /> Lock</>}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={isLocked || isContactRoot} onClick={() => dispatch('archive')}>
            <Archive className="w-3.5 h-3.5 mr-2" /> Archive
          </DropdownMenuItem>
          <DropdownMenuItem disabled={isLocked || isContactRoot} className="text-destructive"
            onClick={() => dispatch('delete')}>
            <Trash2 className="w-3.5 h-3.5 mr-2" /> Move to Trash
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent className="w-56">{items}</ContextMenuContent>
      </ContextMenu>
      {!hideTrigger && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon" variant="ghost"
              className="h-7 w-7 opacity-0 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
              data-testid={`folder-actions-${folder.folder_id}`}
            >
              <MoreVertical className="w-3.5 h-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
            {dropdownItems}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}
